<template>
  <!-- Main container with h-full to take full height -->
  <div class="flex flex-col h-full">
    <!-- Close button -->
    <button @click="closeDialog" class="self-start text-gray-400 hover:text-gray-500 p-2">
      <i class="fas fa-times h-5 w-5"></i>
    </button>

    <!-- Auth Modal component -->
    <AuthModal 
      :isOpen="showAuthModal" 
      :message="authModalMessage"
      @close="showAuthModal = false"
      @login="handleLogin"
    />

    <!-- TabGroup container taking remaining height -->
    <div class="flex flex-col flex-1 overflow-hidden">
      <TabGroup :defaultIndex="showAlertListTabFirst ? 1 : 0" :selectedIndex="selectedTab" @change="changeTab" as="div" class="flex flex-col h-full">
        <!-- Sticky Tab navigation -->
        <TabList class="sticky top-0 flex flex-row justify-center z-10 space-x-2 border-b bg-white">
          <!-- Create/Edit Alert -->
          <Tab v-slot="{ selected }" as="template">
            <button :class="['px-4 py-2', selected ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500']">
              <template v-if="isEditingAlert()">
                Edit Alert
              </template>
              <template v-else-if="isCreatingNewAlert()">
                Create Alert
              </template>
              <template v-else-if="isListingAlerts()">
                Show Alerts
              </template>
            </button>
          </Tab>

          <!-- Show Alerts -->
          <Tab v-slot="{ selected }" as="template" v-if="isInEventsTable()">
            <button :class="['px-4 py-2', selected ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500']">
              Alerts
            </button>
          </Tab>
          
          <!-- Alert History -->
          <Tab v-slot="{ selected }" as="template" v-if="isInEventsTable()">
            <button :class="['px-4 py-2', selected ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500']">
              Notifications
            </button>
          </Tab>
        </TabList>

        <!-- Scrollable Tab panels container -->
        <TabPanels class="flex-1 overflow-hidden">
          <!-- Create/Edit Alert -->
          <TabPanel class="h-full flex flex-col">
            <!-- Scrollable content area -->
            <div class="flex-1 overflow-y-auto p-4 space-y-3">
              <!-- Header -->
              <div class="flex flex-col items-center font-light text-xl">
                <div v-if="isEditingAlert()">
                  {{ getTargetTitleFromSubscription(subscriptionToEdit) }}
                </div>
                <div v-else>
                  {{ getCreateTargetTitle() }}
                </div>
                <div class="text-sm text-gray-500" v-tippy="{ content: getDialogDescription(), delay: [300, 0], allowHTML: true }">
                  {{ scope }} Alert <i class="fas fa-question-circle text-gray-400 text-sm ml-1"></i>
                  <span v-if="scope !== SubscriptionScope.WATCHLIST && isEditingAlert()" class="text-sm"> in '{{ this.getWatchlistName(subscriptionToEdit.watchlistId) }}'</span>
                  <span v-if="scope !== SubscriptionScope.WATCHLIST && !isEditingAlert()" class="text-sm"> in '{{ this.getWatchlistName(currentWatchlist.id) }}'</span>
                </div>
              </div>

              <hr class="border-gray-100 mb-4" />
              <!-- Condition Listbox -->
              <div class="flex items-center justify-between gap-4">
                <label class="text-base font-medium text-gray-700 whitespace-nowrap">Condition</label>
                <Listbox v-model="selectedCondition" class="w-[70%]">
                  <div class="relative">
                    <ListboxButton class="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-left shadow-sm focus:border-blue-500 focus:outline-none flex justify-between items-center">
                      <div class="flex items-center gap-2">
                        <img :src="getNotificationIcon(selectedCondition)" class="w-6 h-6" />
                        <span>{{ getNotificationTitle(selectedCondition) }}</span>
                      </div>
                      <img src="/assets/resize-small.svg" alt="Expand" class="w-6 h-6" />
                    </ListboxButton>
                    <ListboxOptions class="absolute mt-1 w-full rounded-md bg-white shadow-lg border">
                      <ListboxOption v-for="condition in getAvailableSubscriptionTypes()" :key="condition" :value="condition" v-slot="{ active }" :disabled="getDisabledStatus(condition)">
                        <div :class="['px-3 py-2 cursor-pointer flex items-center gap-2', active ? 'bg-blue-50 text-blue-600' : 'text-gray-900']">
                          <img 
                            :src="getNotificationIcon(condition)" 
                            :class="getDisabledStatus(condition) ? 'opacity-30 w-5 h-5' : 'w-5 h-5'"
                            />
                          <span 
                            :class="getDisabledStatus(condition) ? 'opacity-30' : ''"
                            v-tippy="{ content: getConditionTippyText(condition), delay: [300, 0], allowHTML: true }"
                          >{{ getNotificationTitle(condition) }}</span>
                        </div>                              
                      </ListboxOption>
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>

              <hr class="border-gray-100" />

              <!-- Conditional Input Fields -->
              <div v-if="[
                SubscriptionType.GREATER_THAN,
                SubscriptionType.LESS_THAN,
                SubscriptionType.CROSSING_UP,
                SubscriptionType.CROSSING_DOWN,
                SubscriptionType.CROSSING,
                SubscriptionType.PCT_MOVE_UP,
                SubscriptionType.PCT_MOVE_DOWN,
                SubscriptionType.PCT_MOVE
              ].includes(selectedCondition)" class="flex items-center justify-between gap-2 text-base">
                <label class="font-medium text-gray-700 whitespace-nowrap">{{ getNotificationLabel(selectedCondition) }}</label>
                <div class="flex items-center gap-1">
                  <input 
                    type="number" 
                    inputmode="decimal"
                    step="1.00"
                    v-model="thresholdValue"
                    :placeholder="thresholdValue"
                    :style="{ width: `${Math.max(6, String(thresholdValue).length + 4)}ch` }"
                    class="rounded-md border border-gray-300 p-2" 
                  />
                  <span>%</span>
                </div>
              </div>

              <hr v-if="notEventStatusCondition()" class="border-gray-100" />

              <!-- Trigger Type -->
              <div v-if="notEventStatusCondition()" class="flex items-center justify-between gap-4">
                <div class="flex items-center whitespace-nowrap" v-tippy="{
                  content: `
                    <div class='p-2'>
                      <p class='mb-2'><strong>Once:</strong> Alert will fire once and will be deactivated.</p>
                      <p><strong>Every time:</strong> Alert will fire every time the condition is met.</p>
                    </div>
                  `,
                  allowHTML: true,
                }">
                  <label class="text-base font-medium text-gray-700">Trigger</label>
                  <i class="fas fa-question-circle text-gray-400 text-sm ml-1"></i>
                </div>
                <RadioGroup v-model="selectedTriggerType" class="flex gap-4">
                  <RadioGroupOption v-slot="{ checked }" :value="TriggerType.ONE_TIME" as="template">
                    <div :class="['flex items-center cursor-pointer', checked ? 'text-blue-600' : 'text-gray-500']">
                      <div :class="['h-4 w-4 rounded-full border flex items-center justify-center', checked ? 'border-blue-600' : 'border-gray-300']">
                        <div v-if="checked" class="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span class="ml-2">Once</span>
                    </div>
                  </RadioGroupOption>
                  <RadioGroupOption v-slot="{ checked }" :value="TriggerType.RECURRING" as="template">
                    <div :class="['flex items-center cursor-pointer', checked ? 'text-blue-600' : 'text-gray-500']">
                      <div :class="['h-4 w-4 rounded-full border flex items-center justify-center', checked ? 'border-blue-600' : 'border-gray-300']">
                        <div v-if="checked" class="h-2 w-2 rounded-full bg-blue-600" />
                      </div>
                      <span class="ml-2">Every time</span>
                    </div>
                  </RadioGroupOption>
                </RadioGroup>
              </div>

              <hr v-if="notEventStatusCondition()"class="border-gray-100" />

              <!-- Indefinite Alert Switch -->
              <div v-if="notEventStatusCondition()">
                <div class="flex items-center justify-between">
                  <label v-if="isIndefiniteAlert" class="text-xl font-light text-gray-700">Indefinite alert</label>
                  <label v-if="!isIndefiniteAlert" class="text-xl font-light text-gray-700">Expires on</label>
                  <button 
                    type="button"
                    @click="isIndefiniteAlert = !isIndefiniteAlert"
                    :class="[
                      'inline-flex h-6 w-11 items-center rounded-full',
                      isIndefiniteAlert ? 'bg-blue-600' : 'bg-gray-200'
                    ]"
                  >
                    <span
                      :class="[
                        'inline-block h-4 w-4 transform rounded-full bg-white transition',
                        isIndefiniteAlert ? 'translate-x-6' : 'translate-x-1'
                      ]"
                    />
                  </button>
                </div>
                <label v-if="isIndefiniteAlert" class="text-sm font-light text-gray-500 block">Alert will remain active until manually disabled</label>
                <label v-if="!isIndefiniteAlert" class="block text-sm font-light text-gray-700 mb-2">
                  Alert will expire on <span class="font-bold">{{ formattedExpirationDate }}</span>
                </label>
              </div>

              <hr v-if="notEventStatusCondition()" class="border-gray-100" />

              <!-- Notification Channels -->
              <div class="space-y-4">
                <div class="flex items-center gap-2">
                  <label class="block text-base font-medium text-gray-700">Notifications</label>
                  <div v-if="enabledChannelsCount > 0" class="bg-blue-600 text-white text-sm font-mono font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {{ enabledChannelsCount }}
                  </div>
                </div>
                <div 
                  v-for="channel in notificationChannels" 
                  :key="channel.type" 
                  class="flex flex-col w-full"
                >
                  <div class="flex flex-row justify-between cursor-pointer">
                    <div @click="if (channel.type !== NotificationChannelType.TELEGRAM || store.telegramChatId !== null) {
                      channel.enabled = !channel.enabled;
                      if (channel.type === NotificationChannelType.WEBHOOK && channel.enabled) {
                        scrollToWebhookInput();
                      }
                      if (channel.type === NotificationChannelType.DISCORD && channel.enabled) {
                        scrollToDiscordWebhookInput();
                      }
                    }" class="flex items-start">
                      <input 
                        type="checkbox"
                        :id="channel.type"
                        v-model="channel.enabled"
                        :disabled="channel.type === NotificationChannelType.TELEGRAM && store.telegramChatId === null"
                        class="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        @click.stop
                        @change="if (channel.type === NotificationChannelType.WEBHOOK && channel.enabled) { scrollToWebhookInput(); }
                                 if (channel.type === NotificationChannelType.DISCORD && channel.enabled) { scrollToDiscordWebhookInput(); }"
                      />
                      <div class="ml-3">
                        <span :for="channel.type" class="font-medium text-gray-700">{{ channel.type }}</span>
                        <p class="text-sm text-gray-500">{{ channel.description }}</p>
                      </div>
                    </div>
                    <a 
                      v-if="channel.type === NotificationChannelType.TELEGRAM && store.telegramChatId === null" 
                      :href="`https://t.me/NevuaMarketsBot?start=${store.telegramNonce}`" 
                      target="_blank"
                      rel="noopener noreferrer"
                      class="font-normal border border-gray-300 rounded-md px-2 py-2 text-sm hover:border-gray-500 hover:font-normal text-center justify-self-center"
                      @click="connectTelegram()"
                      v-tippy="{ content: 'Click to connect your Telegram account. You will need to do it only once.', delay: [300, 0], allowHTML: true }"
                      >
                        <span v-if="checkTelegramInterval === null && store.isAuthenticated">Enable Telegram</span>
                        <span v-else="">Enabling... <i class="fas fa-spinner fa-spin" /></span>
                    </a>
                  </div>

                  <!-- Discord webhook input placed directly under Discord option -->
                  <div v-if="channel.type === NotificationChannelType.DISCORD && channel.enabled" class="mt-2 ml-7" ref="discordWebhookInputContainer">
                    <label for="discordWebhookUrl" class="block text-sm font-medium text-gray-700 mb-1">Discord Webhook URL</label>
                    <input 
                      type="text" 
                      id="discordWebhookUrl"
                      v-model="discordWebhookUrl"
                      placeholder="https://discord.com/api/webhooks/..."
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none',
                        discordWebhookUrl ? (isDiscordWebhookValid() ? 'border-green-500 focus:ring-green-500' : 'border-red-500 focus:ring-red-500') : 'border-gray-300 focus:ring-blue-500'
                      ]"
                      ref="discordWebhookInput"
                      @input="validateDiscordWebhook"
                    />
                    <p v-if="discordWebhookUrl && !isDiscordWebhookValid()" class="mt-1 text-sm text-red-600">
                      Please enter a valid Discord webhook URL
                    </p>
                    <p v-else-if="discordWebhookUrl && isDiscordWebhookValid()" class="mt-1 text-sm text-green-600">
                      Valid Discord webhook URL
                    </p>
                  </div>
                </div>
                
                <!-- Webhook URL input field (kept outside the loop) -->
                <div v-if="notificationChannels.find(c => c.type === NotificationChannelType.WEBHOOK && c.enabled)" class="mt-2 ml-7" ref="webhookInputContainer">
                  <label for="webhookUrl" class="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
                  <input 
                    type="text" 
                    id="webhookUrl"
                    v-model="webhookUrl"
                    placeholder="https://your-webhook-endpoint.com"
                    :class="[
                      'w-full px-3 py-2 border rounded-md focus:outline-none',
                      webhookUrl ? (isWebhookValid() ? 'border-green-500 focus:ring-green-500' : 'border-red-500 focus:ring-red-500') : 'border-gray-300 focus:ring-blue-500'
                    ]"
                    ref="webhookInput"
                    @input="validateWebhook"
                  />
                  <p v-if="webhookUrl && !isWebhookValid()" class="mt-1 text-sm text-red-600">
                    Please enter a valid URL (e.g., https://example.com/webhook)
                  </p>
                  <p v-else-if="webhookUrl && isWebhookValid()" class="mt-1 text-sm text-green-600">
                    Valid webhook URL
                  </p>
                </div>
              </div>
            </div>

            <!-- Sticky footer -->
            <div class="flex-shrink-0 justify-between items-center sticky bottom-0 bg-white py-2 border-t px-4">
              <div class="flex justify-between">
                <div>
                  <button 
                    v-if="isEditingAlert()"
                    @click="deleteAlert(subscriptionToEdit)" 
                    class="px-2 py-1 text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed border border-red-600 rounded-md"
                    title="Delete alert"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
                <div class="flex justify-end space-x-3">
                  <button @click="closeDialog" class="px-4 py-2 text-gray-700 hover:text-gray-900">
                    Cancel
                  </button>
                  <button v-if="isCreatingNewAlert()"
                    @click="createNewAlertInAPI()" 
                    :disabled="isCreating || !hasEnabledChannels() || !isWebhookValid() || !isDiscordWebhookValid()"
                    class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {{ actionText() }}
                  </button>
                  <button v-if="isEditingAlert()"
                    @click="editAlertInAPI()"
                    :disabled="isEditing || !hasEnabledChannels() || !isWebhookValid() || !isDiscordWebhookValid()"
                    class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                  {{ actionText() }}
                  </button>
                </div>
              </div>
            </div>
          </TabPanel>

          <!-- Show Alerts -->
          <TabPanel v-if="isInEventsTable()" class="h-full overflow-y-auto">
            <div>
              <div v-if="listSubscriptions().length > 0">

                <!-- Action Pills -->
                <div class="flex justify-center space-x-2 my-2">
                  <button 
                    @click="pauseAllAlerts"
                    disabled
                    class="flex items-center justify-center border bg-white shadow-sm hover:bg-gray-100 rounded-full px-3 text-xs h-8" 
                    type="button"
                    v-tippy="{ content: 'Bulk alert actions are temporarily disabled', delay: [300, 0] }"
                  >
                    <i class="fas fa-pause mr-2"></i>
                    Pause All
                  </button>
                  
                  <button 
                    @click="restartAllAlerts"
                    disabled
                    class="flex items-center justify-center border bg-white shadow-sm hover:bg-gray-100 rounded-full px-3 text-xs h-8" 
                    type="button"
                    v-tippy="{ content: 'Bulk alert actions are temporarily disabled', delay: [300, 0] }"
                  >
                    <i class="fas fa-play mr-2"></i>
                    Restart All
                  </button>

                  <button 
                    @click="deleteAllAlerts"
                    disabled
                    class="flex items-center justify-center border bg-white shadow-sm hover:bg-red-50 rounded-full px-3 text-xs h-8 text-red-800" 
                    type="button"
                    v-tippy="{ content: 'Bulk alert actions are temporarily disabled', delay: [300, 0] }"
                  >
                    <i class="fas fa-trash-alt mr-2"></i>
                    Delete All
                  </button>
                </div>

                <!-- Filter Indicator -->
                <div 
                  v-if="isFilteringSubscriptions()" 
                  class="flex flex-row items-center justify-between mb-4 p-2 rounded-md">
                  <span class="text-sm font-light text-gray-600 w-2/3 whitespace-break-spaces">Showing alerts {{ getFilterTarget() }}</span>
                  <button 
                    @click="$emit('clear-filters')"
                    class="text-xs border border-blue-600 rounded-full px-2 py-1 text-blue-600 hover:text-blue-800"
                    v-tippy="{ content: 'Show all alerts across all watchlists' }"
                  >
                    <i class="fas fa-times"></i>  Remove filter
                  </button>
                </div>
                <div v-else
                class="text-sm font-light text-center text-gray-600 mb-2">
                  Showing all alerts across all watchlists
                </div>
                  

                <div class="space-y-4 p-4">
                  <div v-for="subscription in listSubscriptions()" :key="subscription.id" class="bg-white rounded-lg shadow-sm">
                    <!-- Subscription item -->
                    <div class="grid grid-cols-[1fr,auto] py-1 hover:bg-gray-100 transition-colors duration-200" >
                      <div class="min-w-0" v-tippy="{
                      content: `
                        <div class='p-2'>
                          <p class='mb-1'><strong>${getScopeTitle(subscription)}</strong></p>
                          <p class='mb-1 ml-2'><strong>- Created:</strong> ${formatDate(subscription.createdAt)}</p>
                          <p class='mb-1 ml-2'><strong>- Expires:</strong> ${subscription.expiresAt ? formatDate(subscription.expiresAt) : 'Never'}</p>
                        </div>
                      `,
                      allowHTML: true,
                      delay: [600, 0]
                    }">
                        
                        <!-- Target name -->
                        <div class="flex flex-wrap items-center font-extralight text-lg w-70 whitespace-break-spaces">
                            {{ getTargetTitleFromSubscription(subscription) }}
                        </div>

                        <!-- Notification type and status-->
                        <div class="flex flex-wrap items-center gap-x-1">
                          <img :src="getNotificationIcon(subscription.subscriptionTypeConfig.type)" class="w-5 h-5 mr-1 " />
                          <span class="text-sm font-normal text-gray-900">{{ getNotificationTitle(subscription.subscriptionTypeConfig.type) }}</span>
                          <span v-if="getThresholdValue(subscription)" class="text-sm font-bold">{{ getThresholdValue(subscription) }}</span>
                          <span 
                            :class="[
                              'text-sm border rounded-md px-1 ml-2',
                              subscription.status.enabled ? 'border-green-500 text-green-600' : 'border-red-500 text-red-600'
                            ]"
                            :title="subscription.status.reason"
                          >
                            {{ subscription.status.enabled ? 'Active' : 'Inactive' }}
                          </span>
                        </div>

                        <!-- Scope -->
                        <!-- <div class="flex items-center text-sm text-gray-500 whitespace-break-spaces">
                          <span class="font-thin" v-html="getScopeTitle(subscription)"></span>
                        </div> -->
                      
                        <!-- Last triggered -->
                        <div class="flex items-center space-x-2 text-sm text-gray-600">
                          <span v-if="subscription?.triggerCount > 0" class="font-thin">Count: 
                            <span class="font-normal">{{ subscription.triggerCount }}</span>
                          </span>
                          <span class="font-thin">Last triggered: 
                            <span class="font-normal">{{ formatDistanceToNow(subscription.lastTriggeredAt) }}</span>
                          </span>
                        </div>
                      </div>

                      <!-- Alert actions -->
                      <div class="flex items-center">
                        <!-- Start/Pause Alert -->
                        <div v-tippy="{ content: getActionButtonContent(subscription, 'Start'), delay: [300, 0], allowHTML: true }">
                          <button 
                            @click="subscription.status.enabled ? pauseAlert(subscription) : startAlert(subscription)" 
                            :class="[
                              'w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-500 hover:border hover:border-gray-300 rounded-md',
                              !subscription.status.enabled && !canStartAlert(subscription) ? 'opacity-50 cursor-not-allowed' : ''
                        ]"
                            :disabled="(!subscription.status.enabled && !canStartAlert(subscription)) || isStartingOrPausingAlert">
                            <i :class="[
                              subscription.status.enabled ? 'fas fa-pause' : 'fas fa-play',
                              !subscription.status.enabled && !canStartAlert(subscription) ? 'opacity-80' : ''
                              ]">
                            </i>
                          </button>
                        </div>

                        <!-- Edit Alert -->
                          <div v-tippy="{ content: getActionButtonContent(subscription, 'Edit'), delay: [300, 0], allowHTML: true }">
                          <button 
                            @click="editAlert(subscription)"
                            :class="[
                              'w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-500 hover:border hover:border-gray-300 rounded-md',
                              !canEditAlert(subscription) ? 'opacity-50 cursor-not-allowed' : ''
                            ]" 
                            title="Edit"
                          >
                            <i :class="[
                              'fas fa-edit',
                              !canEditAlert(subscription) ? 'opacity-80' : ''
                            ]"></i>
                          </button>
                        </div>

                        <!-- Delete Alert -->
                        <button 
                          @click="deleteAlert(subscription)" 
                          class="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:border hover:border-gray-300 rounded-md" 
                          title="Delete"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-gray-500 text-center py-8 flex items-center justify-center">
                No alerts found
              </div>
              <div v-if="listSubscriptions().length > 0" class="flex items-center justify-center py-1 text-gray-400/50 text-sm">
                <span class="w-16 border-t border-gray-200"></span>
                <span class="px-4">End of results</span>
                <span class="w-16 border-t border-gray-200"></span>
              </div>
            </div>
          </TabPanel>

          <!-- Notification History -->
          <TabPanel class="h-full overflow-y-auto">
            <div v-if="listNotifications().length === 0" class="text-gray-500 text-center py-8  flex items-center justify-center">
              Notification log will appear here
            </div>
            <div v-else >

              <!-- Action Pills -->
              <div class="flex justify-center space-x-2 my-2">
                <button 
                  @click="markAllNotificationsAsRead(listNotifications())"
                  class="flex items-center justify-center border bg-white shadow-sm hover:bg-gray-100 rounded-full px-3 text-xs h-8" 
                  type="button"
                >
                  <i class="fas fa-check mr-2"></i>
                  Mark All as Read
                </button>
                
                <button 
                  @click="deleteNotifications(listNotifications())"
                  class="flex items-center justify-center border bg-white shadow-sm hover:bg-red-50 rounded-full px-3 text-xs h-8 text-black" 
                  type="button"
                >
                  <i class="fas fa-broom mr-2"></i>
                  Clear All
                </button>
              </div>

              <!-- Filter Indicator -->
              <div 
                v-if="isFilteringSubscriptions()" 
                class="flex flex-row items-center justify-between mb-4 p-2 rounded-md">
                <span class="text-sm font-light text-gray-600 w-2/3 whitespace-break-spaces">Showing notifications {{ getFilterTarget() }}</span>
                <button 
                  @click="$emit('clear-filters')"
                  class="text-xs border border-blue-600 rounded-full px-2 py-1 text-blue-600 hover:text-blue-800"
                  v-tippy="{ content: 'Show all notifications across all watchlists' }"
                >
                  <i class="fas fa-times"></i>  Remove filter
                </button>
              </div>
              <div v-else
                class="text-sm font-light text-center text-gray-600 mb-4">
                Showing all notifications across all watchlists
              </div>

              <!-- Entry for each notification - #f2f7fa -->
              <div v-for="notification in listNotifications()" :key="notification.id" 
                :class="['group py-2 px-4 border-b-2 border-gray-200', isUnread(notification) ? 'bg-[#d5e9f5]' : 'bg-white']"
                @mouseenter="markNotificationAsRead(notification.id)"
              >
                <div v-if="notification.type === 'price_notification'"
                class="flex justify-between">
                  <!-- First Column -->
                  <div 
                    class="flex-1" 
                    v-tippy="{ content: `Created at: ${notification.createdAt} <br/> ${formatDistanceToNow(notification.createdAt)}`, delay: [600, 0], allowHTML: true }"
                  >
                    <!-- First Row -->
                    <div 
                    class="text-sm font-light text-gray-900 whitespace-normal max-w-96">
                      '<a :href="`https://polymarket.com/event/${notification.polymarketSlug}`" target="_blank"><strong class="underline">{{ notification.marketQuestion }}</strong></a>' is <span class="font-bold">{{ getDetectedValue(notification) }}</span>
                    </div>
                    <!-- Second Row -->
                    <div class="text-xs text-gray-600 mt-2 whitespace-normal max-w-96">
                      {{ notification.subscriptionTypeConfig.type }} {{ getConfigValue(notification) }}%
                      <span v-if="!isFilteringSubscriptions()">in watchlist '{{ getWatchlistName(notification.watchlistId) }}'</span>
                    </div>
                  </div>
                  
                  <!-- Second Column - Clear Button -->
                  <div class="flex justify-center">
                    <button
                      @click="deleteNotifications([notification])"
                      class="text-black opacity-100 group-hover:opacity-100 transition-opacity duration-200"
                      v-tippy="{content: 'Clear'}"
                    >
                      <i class="fa-solid fa-broom"></i>
                    </button>
                  </div>
                </div>
                <div v-if="notification.type === 'event_status_notification'"
                class="flex justify-between">
                  <!-- First Column -->
                  <div 
                    class="flex-1" 
                    v-tippy="{ content: `Created at: ${notification.createdAt} <br/> ${formatDistanceToNow(notification.createdAt)}`, delay: [600, 0], allowHTML: true }"
                  >
                    <!-- First Row -->
                    <div 
                      v-if="notification.eventStatusScope === EventStatusScope.EVENT"
                      class="text-sm font-light text-gray-900 whitespace-normal max-w-96">
                      {{ notification.eventStatusScope }} '<a class="underline " :href="`https://polymarket.com/event/${notification.polymarketSlug}`" target="_blank">{{ notification.eventTitle }}</a>' is <span class="font-bold">{{ notification.triggerType }}.</span>
                      <br><span class="text-xs text-gray-500 font-bold" >{{ getOutcomes(notification) }}</span>
                    </div>
                    <div 
                      v-if="notification.eventStatusScope === EventStatusScope.MARKET"
                      class="text-sm font-light text-gray-900 whitespace-normal max-w-96">
                      {{ notification.eventStatusScope }} <a class="underline " :href="`https://polymarket.com/event/${notification.polymarketSlug}`" target="_blank">{{ notification.marketQuestion }}</a> is <span class="font-bold">{{ notification.triggerType }}.</span>
                      <br><span class="text-xs text-gray-500 font-bold" >{{ getOutcomes(notification) }}</span>
                    </div>
                  </div>
                  
                  <!-- Second Column - Clear Button -->
                  <div class="flex justify-center">
                    <button
                      @click="deleteNotifications([notification])"
                      class="text-black opacity-100 group-hover:opacity-100 transition-opacity duration-200"
                      v-tippy="{content: 'Clear'}"
                    >
                      <i class="fa-solid fa-broom"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-center py-4 text-gray-400/50 text-sm">
                <span class="w-16 border-t border-gray-200"></span>
                <span class="px-4">End of results</span>
                <span class="w-16 border-t border-gray-200"></span>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  </div>
</template>

<script>
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { RadioGroup, RadioGroupOption } from '@headlessui/vue'
import { SubscriptionType, SubscriptionScope, TriggerType, SubscriptionStatusReason  } from '../types/Subscriptions'
import { NotificationChannelType, EventStatusScope } from '../types/Notifications'
import { formatDistanceToNowStrict } from 'date-fns';
import { AxiosError } from 'axios';
import ms from 'ms'
import AuthModal from './AuthModal.vue'
import { confirmAuth } from '../auth';
import * as Sentry from '@sentry/vue';

import { 
  showDialog, 
  store, 
  updateSubscriptionInGlobalStateAndTableState, 
  addSubscriptionInGlobalStateAndTableState,
  pauseAlertInAPI,
  startAlertInAPI,
  canStartAlert,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  unreadNotificationsCount,
  deleteNotificationsInAPI,
  getOutcomes
} from '../store'
import { delay, isNumber, size } from 'lodash'
import apiClient from '../utils/axios'

export default {
  name: 'SubscriptionsDialogContent',
  emits: ['close', 'clear-filters', 'edit', 'delete'],
  
  components: {
    TabGroup,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    RadioGroup,
    RadioGroupOption,
    AuthModal,
  },
/*
  Component has the following states:
    - State: currentWatchlist is set
    - events size is > 0
  1. *Create* new alert for watchlist, event or market. This happens *only in events table*. In this state:
    a. List: If watchlist, we show all alerts and logs
    b. List: If event, we filter existing alerts and logs by eventId
    c. List: If market, we filter existing alerts and logs by marketId
    ** states: currentWatchlist, eventsToCreateAlertFor, listAlertsForMarketId, listAlertsForEventId

  2. *List* alerts for market or event. This happens *only in events table*. In this state:
      - State: currentWatchlist is set
    a. List: If watchlist, we show all alerts and logs
    b. List: If event, we filter existing alerts and logs by eventId
      - State: listAlertsForEventId is set  
    c. List: If market, we filter existing alerts and logs by marketId
      - State: listAlertsForMarketId is set
    d. Edit: Can happen if user selects to edit an alert from the list of alerts
      - State: subscriptionToEdit is set
    ** states: currentWatchlist, listAlertsForMarketId, listAlertsForEventId

  3. *Edit* existing alert. This can happen in *either* subscriptions table *or* events table. In this state:
    a. If we edit from subscriptions table, we only show the edit tab, no listing alerts is shown
      - State: no currentWatchlist, no events, no listAlertsForMarketId, no listAlertsForEventId
      - State: subscriptionToEdit is set
    b. For the events table, editing is only possible when listing alerts for a specific event or market
      - State: currentWatchlist, listAlertsForMarketId, listAlertsForEventId
      - State: subscriptionToEdit is set
    state props:
      - currentWatchlist: only in events table
      - events: only in events table, when *creating* a new alert
      - listAlertsForMarketId: only in events table, when *listing* alerts for a market
      - listAlertsForEventId: only in events table, when *listing* alerts for an event
      - subscriptionToEdit: both in events and subscriptions table, when *editing* an alert
*/
  props: {
    scope: {
      type: String,
      default: SubscriptionScope.WATCHLIST,
      validator: (value) => Object.values(SubscriptionScope).includes(value),
      required: false
    },
    targetTitle: {
      type: String,
      default: null,
      required: false
    },
    currentWatchlist: {
      type: Object,
      default: null,
      required: false
    },
    eventsToCreateAlertFor: {
      type: Object,
      default: null,
      required: false
    },
    subscriptionToEdit: {
      type: Object,
      default: null,
      required: false
    },
    isOpen: {
      type: Boolean,
      default: false,
      required: false
    },
    listAlertsForMarketId: {
      type: String,
      default: null,
      required: false
    },
    listAlertsForEventId: {
      type: String,
      default: null,
      required: false
    },
    listAlertsForWatchlistId: {
      type: String,
      default: null,
      required: false
    },
    showAlertListTabFirst: {
      type: Boolean,
      default: false,
      required: true
    },
    price: {
      type: Number,
      default: null,
      required: false
    },
  },
  created() {
  },

  data() {
    return {
      store,
      checkTelegramInterval: null,
      SubscriptionType,
      SubscriptionScope,
      EventStatusScope,
      TriggerType,
      NotificationChannelType,
      availableSubscriptionTypes: [
        SubscriptionType.GREATER_THAN,
        SubscriptionType.LESS_THAN,
        SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES,
        SubscriptionType.BIG_SPIKE,
        SubscriptionType.CROSSING_UP,
        SubscriptionType.CROSSING_DOWN,
        SubscriptionType.CROSSING,
        // SubscriptionType.PCT_MOVE_UP,
        // SubscriptionType.PCT_MOVE_DOWN,
        // SubscriptionType.PCT_MOVE,
      ],
      watchlistId: null,
      selectedCondition: SubscriptionType.GREATER_THAN,
      thresholdValue: this.getPrice() || 80,
      selectedTriggerType: TriggerType.ONE_TIME,
      notificationChannels: [
        {
          type: NotificationChannelType.IN_APP,
          enabled: true,
          description: 'Receive notifications in the app',
        },
        {
          type: NotificationChannelType.TELEGRAM,
          enabled: false,
          description: 'Receive notifications via Telegram',
        },
        {
          type: NotificationChannelType.DISCORD,
          enabled: false,
          description: 'Receive notifications via Discord',
        },
        {
          type: NotificationChannelType.WEBHOOK,
          enabled: false,
          description: 'Receive notifications via webhook',
        },
        // {
        //   type: NotificationChannelType.EMAIL,
        //   enabled: false,
        //   description: 'Receive notifications via email',
        // },
      ],
      expirationDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().slice(0, 10),
      isIndefiniteAlert: false,
      showScrollButton: false,
      isStartingOrPausingAlert: false,
      isCreating: false,
      isEditing: false,
      selectedTab: undefined,
      showAuthModal: false,
      authModalMessage: 'Please login to create alerts',
      webhookUrl: '',
      discordWebhookUrl: '',
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(newVal) {
        if (newVal == false) {
          this.closeDialog();
        }
      },
      showAlertListTabFirst: {
        immediate: true,
        handler(newVal) {
          // If true, select tab 1 (Alert History)
          // If false, select tab 0 (Alerts)
          this.selectedTab = newVal ? 1 : 0
        }
      }
    },

    selectedCondition(newValue) {
      const pct_subscriptions = [
        SubscriptionType.PCT_MOVE_UP,
        SubscriptionType.PCT_MOVE_DOWN,
        SubscriptionType.PCT_MOVE,
      ]

      // If creating a new alert, set default threshold value based on subscription type
      if (!this.subscriptionToEdit) {
        if (pct_subscriptions.includes(newValue) ) {
          this.thresholdValue = 50;
        } else {
          this.thresholdValue = 80;
        }
      } else {
        this.selectedCondition = newValue;
      }
    },

    thresholdValue(newValue) {
      if (!isNumber(newValue) || newValue < 0) {
        this.thresholdValue = 0;
        return;
      } else {
        this.thresholdValue = newValue;
      }

    },

    subscriptionToEdit: {
      immediate: true,
      handler(subscription) {
        if (subscription) {
          this.initializeFromSubscription(subscription);
        }
      }
    }
  },
  mounted() {
    //call confirmAuth to fetch telegramChatId if it's not already set
    setTimeout(async () => {
      await confirmAuth();
    }, 0);
  },

  computed: {
    minDate() {
      return new Date().toISOString().slice(0, 10);
    },

    maxDate() {
      const date = new Date();
      date.setMonth(date.getMonth() + 1);
      return date.toISOString().slice(0, 10);
    },

    formattedExpirationDate() {
      if (this.isIndefiniteAlert) {
        return 'Indefinite';
      } else {
        const date = new Date(this.expirationDate);
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'UTC',
        }) + ' UTC';
      }
    },


  },

  methods: {
    getOutcomes,
      hasEnabledChannels() {
      return this.notificationChannels.some(channel => channel.enabled);
    },

    isWebhookValid() {
      // If webhook isn't enabled, consider it valid
      const webhookEnabled = this.notificationChannels.find(c => c.type === NotificationChannelType.WEBHOOK && c.enabled);
      if (!webhookEnabled) {
        return true;
      }
      
      // If webhook is enabled, URL must be provided and valid
      const urlPattern = /^https?:\/\/.+/i;
      const isValid = this.webhookUrl && urlPattern.test(this.webhookUrl);
      return isValid;
    },

    isDiscordWebhookValid() {
      // If Discord isn't enabled, consider it valid
      const discordEnabled = this.notificationChannels.find(c => c.type === NotificationChannelType.DISCORD && c.enabled);
      if (!discordEnabled) {
        return true;
      }
      
      // If Discord is enabled, URL must be provided and valid
      // Discord webhook URLs have a specific format: https://discord.com/api/webhooks/...
      const discordUrlPattern = /^https:\/\/discord\.com\/api\/webhooks\/.+/i;
      const isValid = this.discordWebhookUrl && discordUrlPattern.test(this.discordWebhookUrl);
      return isValid;
    },

    validateWebhook() {
      // Force re-evaluation of isWebhookValid
      console.log('Validating webhook URL:', this.webhookUrl);
      return this.isWebhookValid();
    },

    validateDiscordWebhook() {
      // Force re-evaluation of isDiscordWebhookValid
      console.log('Validating Discord webhook URL:', this.discordWebhookUrl);
      return this.isDiscordWebhookValid();
    },

    async connectTelegram() {
      console.log('connectTelegram');
      this.checkTelegramInterval = setInterval(async () => {
        await confirmAuth();
        if (store.telegramChatId !== null) {
          this.notificationChannels.find(channel => channel.type === NotificationChannelType.TELEGRAM).enabled = true;

          clearInterval(this.checkTelegramInterval);
          this.checkTelegramInterval = null;
        }
      }, 2000);
    },
    
    getPrice() {
      if (this.price === null) {
        return null;
      }
      if (this.price < 0) {
        return 0;
      }
      if (this.price > 100) {
        return 100;
      }
      return Number(this.price.toFixed(2));
    },
    getAvailableSubscriptionTypes() {
      const watchlistSubs = this.store.nevuaSubscriptions.filter(subscription => subscription.subscriptionScope === SubscriptionScope.WATCHLIST && subscription.watchlistId === this.currentWatchlist.id);

      const hasMarketStatusSub = watchlistSubs.some(subscription => subscription.subscriptionTypeConfig.type === SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES);

      return this.availableSubscriptionTypes.filter(condition => {
        if (hasMarketStatusSub && condition === SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES) {
          return false
        }
        return true;
      });
    },
    getDisabledStatus(condition) {
      const disabledConditions = [
      SubscriptionType.CROSSING_UP,
        SubscriptionType.CROSSING_DOWN,
        SubscriptionType.CROSSING,
        SubscriptionType.BIG_SPIKE,
      ]
      return disabledConditions.includes(condition);
    },
    getConditionTippyText(condition) {
      if (this.getDisabledStatus(condition)) {
        return 'Coming soon';
      }
      return undefined;
    },
    markAllNotificationsAsRead,
    markNotificationAsRead,
    unreadNotificationsCount,
    async deleteNotifications(notifications) {
      const notificationIds = notifications.map(notification => notification.id);
      store.notifications = store.notifications.filter(notification => !notificationIds.includes(notification.id));
      await deleteNotificationsInAPI(notificationIds);
    },
    canStartAlert(subscription) {
      return canStartAlert(subscription);
    },
    ms(obj) {
      return ms(obj);
    },
    size(obj) {
      return size(obj);
    },
    formatDistanceToNow(dateString) {
      if (!dateString) {
        return 'Never';
      }
      try {
        return formatDistanceToNowStrict(new Date(dateString), { addSuffix: true });
      } catch (error) {
        console.warn(`Invalid date format for: ${dateString}`);
        return 'Never';
      }
    },
    notEventStatusCondition() {
      return this.selectedCondition !== SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES
    },
    actionText() {
        if (this.subscriptionToEdit) {
            return this.isEditing ? 'Updating...' : 'Update'
        }
        if (this.isCreating) {
            return 'Creating...'
        }
        return 'Create'
    },

    getDialogDescription() {
      switch (this.scope) {
        case SubscriptionScope.WATCHLIST:
          return `This alert applies to all existing and any new markets added to this watchlist.`;
        case SubscriptionScope.EVENT:
          return `This alert applies to all existing and any new markets added to this event.`;
        case SubscriptionScope.MARKET:
          return `This alert applies to this market only.`;
      }
    },

    getNotificationIcon(type) {
      switch (type) {
        case SubscriptionType.GREATER_THAN:
          return '/assets/alerts/greater-than.svg'
        case SubscriptionType.LESS_THAN:
          return '/assets/alerts/less-than.svg'
        case SubscriptionType.BIG_SPIKE:
          return '/assets/alerts/big-spike.svg'
        case SubscriptionType.CROSSING:
          return '/assets/alerts/crossing.svg'
        case SubscriptionType.CROSSING_UP:
          return '/assets/alerts/crossing-up.svg'
        case SubscriptionType.CROSSING_DOWN:
          return '/assets/alerts/crossing-down.svg'
        case SubscriptionType.PCT_MOVE:
          return '/assets/alerts/pct-move.svg'
        case SubscriptionType.PCT_MOVE_UP:
          return '/assets/alerts/pct-up.svg'
        case SubscriptionType.PCT_MOVE_DOWN:
          return '/assets/alerts/pct-down.svg'
        case SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES:
          return '/assets/alerts/event-status.svg'
        default:
          return '/assets/alerts/pct-up.svg'
      }
    },

    getDetectedValue(notification) {
      return `${(notification.detectedValue * 100).toFixed(2)}%`
    },
    
    getConfigValue(notification) {
      return notification.subscriptionTypeConfig.config.params.threshold_percent
    },

    isUnread(notification) {
      return notification.channels.some(channel => channel.status !== 'READ' && channel.type === NotificationChannelType.IN_APP);
    },

    getNotificationTitle(type) {
      const titles = {
        [SubscriptionType.GREATER_THAN]: 'Greater Than',
        [SubscriptionType.LESS_THAN]: 'Less Than',
        [SubscriptionType.BIG_SPIKE]: 'Z-score Spike Detector',
        [SubscriptionType.CROSSING_UP]: 'Crossing Up',
        [SubscriptionType.CROSSING_DOWN]: 'Crossing Down',
        [SubscriptionType.CROSSING]: 'Crossing',
        [SubscriptionType.PCT_MOVE]: 'Move %',
        [SubscriptionType.PCT_MOVE_UP]: 'Move Up %',
        [SubscriptionType.PCT_MOVE_DOWN]: 'Move Down %',
        [SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES]: 'New and Closed Events',
      }
      return titles[type] || 'Unknown'
    },

    getNotificationLabel(type) {
      const labels = {
        [SubscriptionType.GREATER_THAN]: 'Notify me when odds exceed',
        [SubscriptionType.LESS_THAN]: 'Notify me when odds drop below',
        [SubscriptionType.CROSSING_UP]: 'Notify me when odds cross above',
        [SubscriptionType.CROSSING_DOWN]: 'Notify me when odds cross below',
        [SubscriptionType.CROSSING]: 'Notify me when odds cross',
        [SubscriptionType.PCT_MOVE_UP]: 'Notify me when odds increase by',
        [SubscriptionType.PCT_MOVE_DOWN]: 'Notify me when odds decrease by',
        [SubscriptionType.PCT_MOVE]: 'Notify me when odds change by',
        [SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES]: 'Notify me on new or closed events',
      }
      return labels[type] || 'Unknown'
    },

    resetForm() {
      this.selectedCondition = SubscriptionType.GREATER_THAN;
      this.thresholdValue = 80;
      this.selectedTriggerType = TriggerType.ONE_TIME;
      this.notificationChannels.forEach(channel => {
        if (channel.type === NotificationChannelType.IN_APP) {
          channel.enabled = true;
        } else {
          channel.enabled = false;
        }
      });
      this.expirationDate = new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().slice(0, 10);
      this.isIndefiniteAlert = false;
      this.webhookUrl = '';
      this.discordWebhookUrl = '';
    },

    initializeFromSubscription(subscription) {
      this.selectedCondition = subscription.subscriptionTypeConfig.type;
      this.selectedTriggerType = subscription.triggerType;
      this.isIndefiniteAlert = !subscription.expiresAt;
      this.expirationDate = subscription.expiresAt ? new Date(subscription.expiresAt).toISOString().slice(0, 10) : this.expirationDate;
      this.watchlistId = subscription.watchlistId;
      
      // Initialize notification channels
      this.notificationChannels = this.notificationChannels.map(channel => ({
        ...channel,
        enabled: subscription.channels.some(c => c.type === channel.type)
      }));

      // Initialize webhookUrl if available
      const webhookChannel = subscription.channels.find(c => c.type === NotificationChannelType.WEBHOOK);
      console.log('webhookChannel', JSON.stringify(webhookChannel));
      if (webhookChannel && webhookChannel.settings && webhookChannel.settings.webhookUrl) {
        this.webhookUrl = webhookChannel.settings.webhookUrl;
      }

      // Initialize discordWebhookUrl if available
      const discordChannel = subscription.channels.find(c => c.type === NotificationChannelType.DISCORD);
      console.log('discordChannel', JSON.stringify(discordChannel));
      if (discordChannel && discordChannel.settings && discordChannel.settings.discordWebhookUrl) {
        this.discordWebhookUrl = discordChannel.settings.discordWebhookUrl;
      }

      // Initialize values based on subscription type
      const config = subscription.subscriptionTypeConfig.config;
      this.thresholdValue = config.params.threshold_percent;
    },

    closeDialog() {
      this.$emit('close');
      this.resetForm();
    },

    getSubscriptionTypeConfig() {
        let subscriptionTypeConfig;

        switch (this.selectedCondition) {
          case SubscriptionType.GREATER_THAN:
          case SubscriptionType.LESS_THAN:
          case SubscriptionType.CROSSING_UP:
          case SubscriptionType.CROSSING_DOWN:
          case SubscriptionType.CROSSING:
          case SubscriptionType.PCT_MOVE_UP:
          case SubscriptionType.PCT_MOVE_DOWN:
          case SubscriptionType.PCT_MOVE:
          subscriptionTypeConfig = {
              type: this.selectedCondition,
              config: {
                params: {
                  threshold_percent: this.thresholdValue,
                }
              }
            }
            break;
          case SubscriptionType.EVENT_AND_MARKET_OPEN_STATUS_CHANGES:
            subscriptionTypeConfig = {
              type: this.selectedCondition,
              config: {
                params: {
                }
              }
            }
            break;
          default:
            throw new Error('Unknown subscription type');
        }
        
      return subscriptionTypeConfig;
    },

    getTargetTitleFromSubscription(subscription) {
      switch (subscription.subscriptionScope) {
        case SubscriptionScope.WATCHLIST:
          return this.getWatchlistName(subscription.watchlistId);
        case SubscriptionScope.EVENT:
          return subscription.eventTitle;
        case SubscriptionScope.MARKET:
          return subscription.marketQuestion;
        default:
          return 'Unknown';
      }
    },

    getCreateTargetTitle() {
      if (this.scope === SubscriptionScope.WATCHLIST) {
        return this.getWatchlistName(this.currentWatchlist.id);
      }
      return this.targetTitle;      
    },

    getScopeTitle(subscription) {
      switch (subscription.subscriptionScope) {
        case SubscriptionScope.WATCHLIST:
          return 'Watchlist alert';
        case SubscriptionScope.EVENT:
          return `Event alert for watchlist '${this.getWatchlistName(subscription.watchlistId)}'`;
        case SubscriptionScope.MARKET:
          return `Market alert for watchlist '${this.getWatchlistName(subscription.watchlistId)}'`;
        default:
          return 'Unknown';
      }
    },

    getThresholdValue(subscription) {
      const config = subscription.subscriptionTypeConfig.config;
      if (this.size(config?.params) === 0) {
        return '';
      }
      return `${config.params.threshold_percent}%`;
    },

    formatDate(dateString) {
      if (!dateString) return 'Never';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC',
        timeZoneName: 'short'
      });
    },

    enabledChannelsCount() {
      return this.notificationChannels.filter(c => c.enabled).length;
    },

    // Derived state properties
    isInEventsTable() {
      return !!this.currentWatchlist;
    },

    isEditingAlert() {
      return !!this.subscriptionToEdit;
    },

    isCreatingNewAlert() {
      return this.isInEventsTable() && !this.isEditingAlert();
    },

    isListingAlerts() {
      // return this.isInEventsTable() && (!!this.listAlertsForMarketId || !!this.listAlertsForEventId);
      return this.isInEventsTable() && !this.isCreatingNewAlert() && !this.isEditingAlert();
    },

    isEditingFromSubscriptionsTable() {
      return this.isEditingAlert() && !this.isInEventsTable();
    },

    isEditingFromEventsTable() {
      return this.isEditingAlert() && this.isInEventsTable();
    },

    shouldShowAllAlerts() {
      return this.isInEventsTable() && !this.listAlertsForMarketId && !this.listAlertsForEventId;
    },

    shouldShowEventAlerts() {
      return this.isInEventsTable() && !!this.listAlertsForEventId && !this.listAlertsForMarketId;
    },

    shouldShowMarketAlerts() {
      return this.isInEventsTable() && !!this.listAlertsForEventId && !!this.listAlertsForMarketId;
    },

    getWatchlistName(watchlistId) {
      const result = store.watchLists?.find(watchlist => watchlist.id === watchlistId)?.name;
      return result || 'Unknown watchlist name';
    },

    isFilteringSubscriptions() {
      return this.listAlertsForEventId || this.listAlertsForMarketId || this.listAlertsForWatchlistId;
    },

    getFilterTarget() {
      // Listing specific Market
      if (this.listAlertsForWatchlistId && this.listAlertsForEventId && this.listAlertsForMarketId) {
        const event = store.tableState.events.find(event => event.id === this.listAlertsForEventId);
        if (!event) {
          // reset filters
          this.$emit('clear-filters');
          return null;
        }
        const market = event.markets.find(market => market.id === this.listAlertsForMarketId);
        if (!market) {
          // reset filters
          this.$emit('clear-filters');
          return null;
        }
        return `for market '${market.question}''`;
      }
      if (this.listAlertsForWatchlistId && this.listAlertsForEventId && !this.listAlertsForMarketId) {
        const event = store.tableState.events.find(event => event.id === this.listAlertsForEventId);
        if (!event) {
          // reset filters
          this.$emit('clear-filters');
          return null;
        }
        return `for event '${event.title}'`;
      }
      if (this.listAlertsForWatchlistId && !this.listAlertsForEventId && !this.listAlertsForMarketId) {
        return `for watchlist '${this.getWatchlistName(this.listAlertsForWatchlistId)}'`;
      }
      return null;
    },

    filterAllSubscriptions(subscription) {
        return !this.listAlertsForEventId && 
                !this.listAlertsForMarketId && 
                !this.listAlertsForWatchlistId;
    },

    filterWatchlistSubscriptions(subscription) {
      return !this.listAlertsForEventId && 
              !this.listAlertsForMarketId && 
              this.listAlertsForWatchlistId;
    },

    filterEventSubscriptions(subscription) {
      return this.listAlertsForEventId && 
              !this.listAlertsForMarketId && 
              this.listAlertsForWatchlistId &&
              subscription.subscriptionScope === SubscriptionScope.EVENT;
    },

    filterMarketSubscriptions(subscription) {
      return this.listAlertsForMarketId && 
              this.listAlertsForEventId && 
              this.listAlertsForWatchlistId &&
              subscription.subscriptionScope === SubscriptionScope.MARKET;
    },

    listSubscriptions() {
      const subscriptions = store.nevuaSubscriptions
        .filter(s => {
          if (this.filterAllSubscriptions(s)) {
            return true;
          }
          if (this.filterWatchlistSubscriptions(s)) {
            return s.watchlistId === this.listAlertsForWatchlistId;
          }
          if (this.filterEventSubscriptions(s)) {
            return s.eventId === this.listAlertsForEventId;
          }
          if (this.filterMarketSubscriptions(s)) {
            return s.marketId === this.listAlertsForMarketId;
          }
          return false;
        })
        .map(subscription => {
          return {
            ...subscription,
            watchlistName: this.getWatchlistName(subscription.watchlistId)
          }
        });

        // order by scope (Watchlist, event, market) first and then by createdAt, newest first
        return subscriptions.sort((a, b) => {
          const scopeOrder = [SubscriptionScope.WATCHLIST, SubscriptionScope.EVENT, SubscriptionScope.MARKET].indexOf(a.subscriptionScope) - [SubscriptionScope.WATCHLIST, SubscriptionScope.EVENT, SubscriptionScope.MARKET].indexOf(b.subscriptionScope);
          if (scopeOrder !== 0) return scopeOrder;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
    },

    listNotifications() {
      return store.notifications
        .filter(n => {
          if (this.filterAllSubscriptions(n)) {
            return true;
          }
          if (this.filterWatchlistSubscriptions(n)) {
            return n.watchlistId === this.listAlertsForWatchlistId;
          }
          if (this.filterEventSubscriptions(n)) {
            return n.eventId === this.listAlertsForEventId;
          }
          if (this.filterMarketSubscriptions(n)) {
            return n.marketId === this.listAlertsForMarketId;
          }
          return false;
        });
    },

    // Edits subscription in API, in response to button click
    async editAlertInAPI() {      
      try {
        if (size(this.subscriptionToEdit) === 0) {
          throw new Error('No subscription to edit');
        }
        this.isEditing = true;

        const watchlistId = this.subscriptionToEdit.watchlistId;
        const updatedAt = new Date().toISOString()
        const reason = SubscriptionStatusReason.UPDATED

        const baseNotification = {
          status: {
            enabled: true,
            reason: reason
          },
          subscriptionScope: this.subscriptionToEdit.subscriptionScope,
          watchlistId: watchlistId,
          channels: this.notificationChannels
            .filter(c => c.enabled)
            .map(c => ({ 
              type: c.type, 
              settings: c.type === NotificationChannelType.WEBHOOK && this.webhookUrl ? 
                { webhookUrl: this.webhookUrl } : 
                c.type === NotificationChannelType.DISCORD && this.discordWebhookUrl ? 
                { discordWebhookUrl: this.discordWebhookUrl } : 
                undefined
            })),
          updatedAt: updatedAt,
          expiresAt: this.isIndefiniteAlert ? null : new Date(this.expirationDate).toISOString(),
          triggerType: this.selectedTriggerType,
        }

        let subscriptionTypeConfig = this.getSubscriptionTypeConfig();

        const subscription = {
          ...baseNotification,
          subscriptionTypeConfig: subscriptionTypeConfig,
        }

        // Update existing subscription
        const response = await apiClient.post(`https://api.nevua.markets/subscriptions/${this.subscriptionToEdit.subscriptionId}/update`, { subscription });

        if (response.status !== 200) {
          throw new Error('Failed to update alert')
        }
        
        showDialog({
          message: 'Alert updated successfully',
          type: 'success',
          timeout: 3000
        });

        // Refresh subscriptions if we are in a watchlist
        if (size(this.currentWatchlist) > 0) {
          updateSubscriptionInGlobalStateAndTableState(response.data.subscriptions[0], this.currentWatchlist?.id);
        }
        
      } catch (error) {
        Sentry.setExtra('method', 'editAlertInAPI');
        Sentry.captureException(error);
        console.error('Failed to update alert', error)
        showDialog({
          message: `Failed to update alert`,
          type: 'error',
          timeout: 3000
        });
      } finally {
        this.isEditing = false;
        this.closeDialog();
      }   
    },

    // Pauses alert in API, in response to button click
    async pauseAlert(subscription) {
        this.isStartingOrPausingAlert = true;
        await pauseAlertInAPI(subscription, this.currentWatchlist?.id);
        this.isStartingOrPausingAlert = false;
    },

    // Returns content for start alert button
    getActionButtonContent(subscription, action='Start') {
      let disabledReasons = []

      if (subscription.status.enabled) {
        return `<div>${action} Alert</div>`
      }

      if (subscription.status.reason === SubscriptionStatusReason.EVENT_CLOSED) {
        disabledReasons.push('<span class="text-red-400">- Event closed</span>')
      }

      if (subscription.status.reason === SubscriptionStatusReason.MARKET_CLOSED) {
        disabledReasons.push('<span class="text-red-400">- Market closed</span>')
      }

      if (subscription.expiresAt && new Date(subscription.expiresAt) < new Date()) {
        disabledReasons.push('<span class="text-red-400">- Alert expired</span>')
      }

      if (disabledReasons.length > 0) {
        return `<div>
          <div class="font-medium mb-1">Cannot ${action.toLowerCase()} alert:</div>
          <div class="space-y-1">${disabledReasons.join('')}</div>
        </div>`
      }

      return `<div>${action} Alert</div>`
    },
/*

    getAlertToggleButtonContent(subscription) {
      if (subscription.status.enabled) {
        return 'Pause Alert';
      }

      let disabledReasons = [];

      if (subscription.status.reason === SubscriptionStatusReason.EVENT_CLOSED) {
        disabledReasons.push('- Event closed');
      }

      if (subscription.status.reason === SubscriptionStatusReason.MARKET_CLOSED) {
        disabledReasons.push('- Market closed');
      }

      if (subscription.expiresAt && new Date(subscription.expiresAt) < new Date()) {
        disabledReasons.push('- Alert expired');
      }

      if (subscription.channels.length === 0) {
        disabledReasons.push('- No channels configured');
      }

      if (disabledReasons.length > 0) {
        return 'Cannot start alert. Reasons: ' + disabledReasons.join('<br>');
      }

      return 'Start Alert';
    },
*/

    canEditAlert(subscription) {
      if (subscription.status.reason === SubscriptionStatusReason.EVENT_CLOSED) {
        return false;
      }

      if (subscription.status.reason === SubscriptionStatusReason.MARKET_CLOSED) {
        return false;
      }

      if (subscription.expiresAt && new Date(subscription.expiresAt) < new Date()) {
        return false;
      }

      return true;
    },

    // Starts alert in API, in response to button click
    async startAlert(subscription) {
      this.isStartingOrPausingAlert = true;
      await startAlertInAPI(subscription, this.currentWatchlist?.id);
      this.isStartingOrPausingAlert = false;
    },

    // Creates new subscription in API, in response to button click
    async createNewAlertInAPI() {
      if (!store.isAuthenticated) {
        this.showAuthModal = true;
        this.authModalMessage = 'Please log in to create alerts and receive real-time notifications';
        return;
      }
      try {
        this.isCreating = true;
        // subtle thing here, we support creating a subscription ONLY while in events table with a watchlist active
        // we need another flow to support creating a subscription while in search mode, without a watchlist active
        const watchlistId = this.currentWatchlist.id;
        const events = this.eventsToCreateAlertFor;
        const name = this.currentWatchlist.name;
        const createdAt = new Date().toISOString()
        const reason = SubscriptionStatusReason.CREATED
        
        const baseNotification = {
          status: {
            enabled: true,
            reason: reason
          },
          subscriptionScope: this.scope,
          watchlistId: watchlistId,
          watchlistName: name,
          events: events,
          channels: this.notificationChannels
            .filter(c => c.enabled)
            .map(c => ({ 
              type: c.type, 
              settings: c.type === NotificationChannelType.WEBHOOK && this.webhookUrl ? 
                { webhookUrl: this.webhookUrl } : 
                c.type === NotificationChannelType.DISCORD && this.discordWebhookUrl ? 
                { discordWebhookUrl: this.discordWebhookUrl } : 
                undefined
            })),
          createdAt: createdAt,
          updatedAt: null,
          expiresAt: this.isIndefiniteAlert ? null : new Date(this.expirationDate).toISOString(),
          triggerType: this.selectedTriggerType,
        }

        let subscriptionTypeConfig = this.getSubscriptionTypeConfig();

        const subscription = {
          ...baseNotification,
          subscriptionTypeConfig: subscriptionTypeConfig,
        }

        // Create new subscription
        const response = await apiClient.post('https://api.nevua.markets/subscriptions', { subscription });

        if (response.status !== 200) {
          throw new Error('Failed to create alert')
        }

        showDialog({
            message: 'Alert created successfully',
            type: 'success',
            timeout: 3000
          });
        
        // Refresh subscriptions if we are in a watchlist
        if (size(this.currentWatchlist) > 0) {
          addSubscriptionInGlobalStateAndTableState(response.data.subscriptions[0], this.currentWatchlist?.id);
        }

      } catch (error) {
        if (error.response?.status === 403) {
          store.freeLimitHit = true;
        } else {
          console.error('Failed to create alert', error)
          showDialog({
            message: `Failed to create alert`,
            type: 'error',
            timeout: 3000
          });
        }
        
        Sentry.setExtra('method', 'createAlert');
        Sentry.captureException(error);
      } finally {
        this.isCreating = false;
        this.closeDialog();
      }
    },

    // Emitted edit event so that parent EventTable will set props
    // Edit to API will happen in this component
    editAlert(subscription) {
      // select first tab
      this.selectedTab = 0;
      this.$emit('edit', subscription);
    },

    // Emitted delete event so that parent EventTable will show modal
    // Deletion will happen in parent EventTable through the modal
    deleteAlert(subscription) {
      this.$emit('delete', subscription.subscriptionId);
    },

    changeTab(index) {
      this.selectedTab = index;
    },

    async pauseAllAlerts() {
      return true
      try {
        let response = await apiClient.post('https://api.nevua.markets/subscriptions/pause-all');
        if (response.status !== 200) {
          throw new Error('Failed to pause alerts');
        }
        showDialog({
          message: 'All alerts have been paused',
          type: 'success',
          timeout: 3000
        });

        await fetchSubscriptionsToGlobalState();
      } catch (error) {
        showDialog({
          message: 'Failed to pause alerts',
          type: 'error',
          timeout: 3000
        });
        return;
      }
    },

    async restartAllAlerts() {
      return true
      try {
        let response = await apiClient.post('https://api.nevua.markets/subscriptions/restart-all');
        if (response.status !== 200) {
          throw new Error('Failed to restart alerts');
        }
        showDialog({
          message: 'All alerts have been restarted',
          type: 'success',
          timeout: 3000
        });

        await fetchSubscriptionsToGlobalState();
      } catch (error) {
        showDialog({
          message: 'Failed to restart alerts',
          type: 'error',
          timeout: 3000
        });
        return;
      }
    },

    async deleteAllAlerts() {
      return true
      this.$emit('delete', 'all');
    },

    handleLogin(provider, email) {
      this.showAuthModal = false;
      
      // Trigger the appropriate authentication method based on the provider
      if (provider === 'google') {
        store.loginWithGoogle();
      } else if (provider === 'twitter') {
        store.loginWithTwitter();
      } else if (provider === 'email' && email) {
        store.loginWithEmail(email);
      }
    },

    scrollToWebhookInput() {
      // Wait for the DOM to update with the webhook input
      this.$nextTick(() => {
        // Scroll to the webhook input container
        if (this.$refs.webhookInputContainer) {
          // Handle case where ref might be an array (when in v-for)
          const container = Array.isArray(this.$refs.webhookInputContainer)
            ? this.$refs.webhookInputContainer[0]
            : this.$refs.webhookInputContainer;
            
          if (container && container.scrollIntoView) {
            // Use scrollIntoView for smooth scrolling
            container.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Focus the input field for better UX
            setTimeout(() => {
              if (this.$refs.webhookInput) {
                const inputEl = Array.isArray(this.$refs.webhookInput)
                  ? this.$refs.webhookInput[0]
                  : this.$refs.webhookInput;
                  
                if (inputEl && typeof inputEl.focus === 'function') {
                  inputEl.focus();
                }
              }
            }, 300);
          }
        }
      });
    },

    scrollToDiscordWebhookInput() {
      // Wait for the DOM to update with the Discord webhook input
      this.$nextTick(() => {
        // Scroll to the Discord webhook input container
        if (this.$refs.discordWebhookInputContainer) {
          // Handle case where ref might be an array (when in v-for)
          const container = Array.isArray(this.$refs.discordWebhookInputContainer) 
            ? this.$refs.discordWebhookInputContainer[0] 
            : this.$refs.discordWebhookInputContainer;
            
          if (container && container.scrollIntoView) {
            // Use scrollIntoView for smooth scrolling
            container.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Focus the input field for better UX
            setTimeout(() => {
              if (this.$refs.discordWebhookInput) {
                const inputEl = Array.isArray(this.$refs.discordWebhookInput)
                  ? this.$refs.discordWebhookInput[0]
                  : this.$refs.discordWebhookInput;
                  
                if (inputEl && typeof inputEl.focus === 'function') {
                  inputEl.focus();
                }
              }
            }, 300);
          }
        }
      });
    },
  }
}
</script>

<style scoped>
input[type="number"] {
  @apply focus:outline-none focus:ring-0 focus:border-gray-300;
}

input[type="number"]::placeholder {
  @apply text-gray-400;
}
</style> 